import { useState, useEffect, useMemo } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getFunctionsErrorBody, formatFunctionsError, isAbortError } from '@/lib/functions-error';
import { formatChartTime } from '@/lib/timezone';
import {
  AreaChart,
  Area,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import type { TooltipProps } from 'recharts';

/** Round down to nearest quarter hour for fallback tick spacing. */
function floorToQuarterHour(ts: number): number {
  const d = new Date(ts);
  const mins = d.getMinutes();
  d.setMinutes(Math.floor(mins / 15) * 15, 0, 0);
  return d.getTime();
}

/** X-axis ticks at 15-min boundaries (:00, :15, :30, :45), or 30-min if data is sparser. Same model as Energy Outlook. */
function getTelemetryXAxisTicks(data: TelemetryDataPoint[]): string[] {
  if (data.length === 0) return [];
  const onQuarter = data.filter((d) => {
    const mins = new Date(d.timestamp).getMinutes();
    return mins === 0 || mins === 15 || mins === 30 || mins === 45;
  });
  if (onQuarter.length >= 4) return onQuarter.map((d) => d.time);
  const onHalf = data.filter((d) => {
    const mins = new Date(d.timestamp).getMinutes();
    return mins === 0 || mins === 30;
  });
  if (onHalf.length >= 2) return onHalf.map((d) => d.time);
  // Fallback: one tick per 15-min slot (closest data point, deduped)
  const firstTs = data[0].timestamp;
  const lastTs = data[data.length - 1].timestamp;
  const startQ = floorToQuarterHour(firstTs);
  const ticks: string[] = [];
  for (let t = startQ; t <= lastTs; t += 15 * 60 * 1000) {
    const closest = data.reduce((best, d) =>
      Math.abs(d.timestamp - t) < Math.abs(best.timestamp - t) ? d : best
    );
    if (!ticks.includes(closest.time)) ticks.push(closest.time);
  }
  return ticks;
}

/** Friendly tooltip: human-readable labels and units (kW, kWh, %). */
function TelemetryTooltip({
  active,
  payload,
  label,
  viewMode,
}: TooltipProps<number, string> & { viewMode: ViewMode }) {
  if (!active || !payload?.length || !label) return null;
  const labelMap: Record<string, { label: string; unit: string }> =
    viewMode === 'energy'
      ? { energyRemaining: { label: 'Energy remaining', unit: 'kWh' }, netConsumption: { label: 'Net consumption', unit: 'kWh' } }
      : viewMode === 'power'
        ? { solarPower: { label: 'Solar', unit: 'kW' }, loadPower: { label: 'Load', unit: 'kW' }, batteryPower: { label: 'Battery', unit: 'kW' }, gridPower: { label: 'Grid', unit: 'kW' } }
        : { soc: { label: 'State of charge', unit: '%' } };
  return (
    <div
      className="rounded-md border border-border bg-wt-bg-tertiary px-3 py-2 shadow-lg"
      style={{ fontFamily: 'var(--font-sans)' }}
    >
      <div className="font-sans text-sm uppercase tracking-wider text-secondary mb-1.5 font-medium">{label}</div>
      <div className="font-mono text-sm text-foreground space-y-0.5">
        {payload.map((entry) => {
          const meta = labelMap[entry.dataKey as string] ?? { label: String(entry.dataKey), unit: '' };
          const value = entry.value as number;
          const formatted = value !== undefined && value !== null ? value.toFixed(1) : '--';
          return (
            <div key={entry.dataKey}>
              <span className="text-secondary mr-1.5">{meta.label}:</span>
              <span>{formatted}</span>
              {meta.unit && <span className="text-secondary ml-0.5">{meta.unit}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

type ViewMode = 'energy' | 'power' | 'state';
type TimeRange = '24h' | '7d';

interface TelemetryPoint {
  recordedAt: string;
  soc: number | null;
  loadPower: number | null;
  pvPower: number | null;
  battPower: number | null;
  gridPower?: number | null;
}

/** Max allowed SOC change per sample (%). Larger deltas → treat as invalid (gap). */
const MAX_SOC_DELTA_PCT = 5;
/** Max plausible energy-remaining change per 5-min step (kWh). Larger deltas → treat as invalid (gap). */
const MAX_ENERGY_DELTA_KWH = 20;
/** Expected bucket interval (5 min). Gaps larger than 2× → insert gap point, break line. */
const EXPECTED_INTERVAL_MS = 5 * 60 * 1000;
const GAP_THRESHOLD_MS = EXPECTED_INTERVAL_MS * 2;
/** If last series point is older than this, append a "now" point from live telemetry so graph and "Current" match. */
const STALE_SERIES_MS = 10 * 60 * 1000;

interface TelemetryDataPoint {
  time: string;
  timestamp: number;
  // Energy view (null = missing / gap)
  energyRemaining: number | null;
  netConsumption: number | null;
  // Power view
  solarPower: number | null;
  loadPower: number | null;
  batteryPower: number | null;
  gridPower: number | null;
  // State view
  soc: number | null;
}

interface TelemetryTimelineProps {
  /** Property ID for fetching telemetry */
  propertyId: string;
  /** Optional plant ID: when set, history is filtered to primary system so SOC matches plants-latest */
  plantId?: string;
  /** Timezone for formatting times */
  timezone?: string;
  /** Configured battery capacity in kWh */
  configuredCapacityKwh?: number;
  /** Reserve threshold in kWh (for energy/state views) */
  reserveKwh?: number;
  /** Current values for header display */
  currentValues?: {
    energy?: number;
    periodStart?: number;
    net?: number;
    solar?: number;
    load?: number;
    battery?: number;
    soc?: number;
    socStart?: number;
    socChange?: number;
  };
}

export function TelemetryTimeline({
  propertyId,
  plantId,
  timezone = 'America/New_York',
  configuredCapacityKwh = 20,
  reserveKwh,
  currentValues,
}: TelemetryTimelineProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('energy');
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<TelemetryDataPoint[]>([]);

  // Fetch telemetry: when plantId is set use unified plant-telemetry series (canonical aggregation); else telemetry-history + frontend bucketing.
  useEffect(() => {
    const fetchHistory = async () => {
      if (!propertyId) return;

      setLoading(true);
      setError(null);

      const t0 = performance.now();
      try {
        const now = new Date();
        const startMs = timeRange === '7d' ? now.getTime() - 7 * 24 * 60 * 60 * 1000 : now.getTime() - 24 * 60 * 60 * 1000;
        const startDate = new Date(startMs);
        const endDate = new Date(now.getTime() + 60 * 1000);

        if (plantId) {
          // Ensure session is valid/refreshed before calling user-authenticated Edge Function (avoids 401 from expired JWT).
          const { data: { user }, error: authError } = await supabase.auth.getUser();
          if (authError || !user) {
            setError('Session expired or invalid. Please log in again.');
            setLoading(false);
            return;
          }
          // Unified API: get_plant_telemetry_series — POST + body so auth header is sent (same as other functions)
          const response = await supabase.functions.invoke('plant-telemetry', {
            method: 'POST',
            body: {
              plantId,
              start: startDate.toISOString(),
              end: endDate.toISOString(),
            },
          });
          const duration = Math.round(performance.now() - t0);
          if (typeof console !== 'undefined' && console.log) {
            console.log(`[perf] plant-telemetry series: ${duration}ms`);
          }
          if (response.error) {
            const body = await getFunctionsErrorBody(response.error);
            if (body) {
              console.warn('[plant-telemetry]', formatFunctionsError(body));
            }
            throw response.error;
          }

          const series = (response.data?.series ?? []) as Array<{
            bucketTs: string;
            plantSocPct: number | null;
            plantEnergyKwh: number | null;
            plantCapacityKwh: number | null;
            plantPvPowerW: number | null;
            plantLoadPowerW: number | null;
            plantBatteryPowerW: number | null;
            plantGridPowerW: number | null;
            plantNetPowerW: number | null;
          }>;

          if (series.length === 0) {
            setChartData([]);
            setLoading(false);
            return;
          }

          const BUCKET_HOURS = 5 / 60;
          let cumulativeNetKwh = 0;
          const pointsFromSeries: TelemetryDataPoint[] = series.map((row, i) => {
            const date = new Date(row.bucketTs);
            const ts = date.getTime();
            const soc = row.plantSocPct ?? null;
            const energyRemaining = row.plantEnergyKwh ?? null;
            const pvW = row.plantPvPowerW ?? null;
            const loadW = row.plantLoadPowerW ?? null;
            const battW = row.plantBatteryPowerW ?? null;
            const gridW = row.plantGridPowerW ?? null;
            const netW = row.plantNetPowerW ?? 0;
            cumulativeNetKwh += (netW * BUCKET_HOURS) / 1000;
            return {
              time: formatChartTime(date, timeRange, timezone),
              timestamp: ts,
              energyRemaining,
              netConsumption: cumulativeNetKwh,
              solarPower: pvW != null ? pvW / 1000 : null,
              loadPower: loadW != null ? loadW / 1000 : null,
              batteryPower: battW != null ? -battW / 1000 : null,
              gridPower: gridW != null ? gridW / 1000 : null,
              soc,
            };
          });

          // Insert gap points when time delta exceeds threshold
          const pointsWithGaps: TelemetryDataPoint[] = [];
          for (let i = 0; i < pointsFromSeries.length; i++) {
            const curr = pointsFromSeries[i];
            if (i > 0) {
              const prev = pointsFromSeries[i - 1];
              if (curr.timestamp - prev.timestamp > GAP_THRESHOLD_MS) {
                const gapTs = prev.timestamp + GAP_THRESHOLD_MS;
                pointsWithGaps.push({
                  time: formatChartTime(gapTs, timeRange, timezone),
                  timestamp: gapTs,
                  energyRemaining: null,
                  netConsumption: null,
                  solarPower: null,
                  loadPower: null,
                  batteryPower: null,
                  gridPower: null,
                  soc: null,
                });
              }
            }
            pointsWithGaps.push(curr);
          }

          let lastValidSoc: number | null = null;
          let lastValidEnergy: number | null = null;
          const pointsSanitized = pointsWithGaps.map((p) => {
            const soc = p.soc;
            let newSoc: number | null = soc ?? null;
            let newEnergy: number | null = p.energyRemaining;
            if (soc != null && p.energyRemaining != null) {
              if (soc <= 0 && lastValidSoc != null && lastValidSoc > 10) {
                newSoc = null;
                newEnergy = null;
              } else if (lastValidSoc != null && Math.abs(soc - lastValidSoc) > MAX_SOC_DELTA_PCT) {
                newSoc = null;
                newEnergy = null;
              } else if (lastValidEnergy != null && Math.abs(p.energyRemaining - lastValidEnergy) > MAX_ENERGY_DELTA_KWH) {
                newSoc = null;
                newEnergy = null;
              } else {
                lastValidSoc = soc;
                lastValidEnergy = p.energyRemaining;
              }
            } else {
              lastValidSoc = null;
              lastValidEnergy = null;
            }
            return { ...p, soc: newSoc, energyRemaining: newEnergy };
          });

          const sampleRate = timeRange === '24h' ? 1 : 2;
          setChartData(pointsSanitized.filter((_, i) => i % sampleRate === 0));
          setLoading(false);
          return;
        }

        // Fallback: telemetry-history (raw rows) + frontend 5-min bucketing when no plantId
        const params = new URLSearchParams({ propertyId, range: timeRange });
        const response = await supabase.functions.invoke(
          `telemetry-history?${params.toString()}`,
          { method: 'GET' }
        );
        const duration = Math.round(performance.now() - t0);
        if (typeof console !== 'undefined' && console.log) {
          console.log(`[perf] telemetry-history: ${duration}ms`);
        }
        if (response.error) throw response.error;

        const telemetry: TelemetryPoint[] = response.data?.telemetry || [];
        if (telemetry.length === 0) {
          setChartData([]);
          setLoading(false);
          return;
        }

        const sortedTelemetry = [...telemetry].sort(
          (a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
        );
        const buckets = new Map<string, { socs: number[]; loads: number[]; pvs: number[]; batts: number[]; grids: number[] }>();
        for (const point of sortedTelemetry) {
          const date = new Date(point.recordedAt);
          const bucketMinutes = Math.floor(date.getMinutes() / 5) * 5;
          date.setMinutes(bucketMinutes, 0, 0);
          const bucketKey = date.toISOString();
          if (!buckets.has(bucketKey)) {
            buckets.set(bucketKey, { socs: [], loads: [], pvs: [], batts: [], grids: [] });
          }
          const b = buckets.get(bucketKey)!;
          if (point.soc != null) b.socs.push(point.soc);
          if (point.loadPower != null) b.loads.push(point.loadPower);
          if (point.pvPower != null) b.pvs.push(point.pvPower);
          if (point.battPower != null) b.batts.push(point.battPower);
          if (point.gridPower != null) b.grids.push(point.gridPower);
        }

        const avg = (arr: number[]) =>
          arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
        const pointsFromBuckets: TelemetryDataPoint[] = [];
        const sortedBuckets = [...buckets.entries()].sort((a, b) => a[0].localeCompare(b[0]));
        let cumulativeNetKwh = 0;

        for (let i = 0; i < sortedBuckets.length; i++) {
          const [bucket, values] = sortedBuckets[i];
          const date = new Date(bucket);
          const ts = date.getTime();
          const oemSocPct = values.socs.length > 0 ? avg(values.socs) : null;
          const loadW = values.loads.length > 0 ? avg(values.loads) : null;
          const pvW = values.pvs.length > 0 ? avg(values.pvs) : null;
          const battW = values.batts.length > 0 ? avg(values.batts) : null;
          const gridW = values.grids.length > 0 ? avg(values.grids) : null;
          if (i > 0) {
            const prevBucket = sortedBuckets[i - 1];
            const prevDate = new Date(prevBucket[0]);
            const deltaMinutes = (ts - prevDate.getTime()) / (1000 * 60);
            if (deltaMinutes <= 30 && battW != null && Math.abs(battW) >= 100) {
              const loadWPrev = sortedBuckets[i - 1][1].loads.length > 0 ? avg(sortedBuckets[i - 1][1].loads) : 0;
              const pvWPrev = sortedBuckets[i - 1][1].pvs.length > 0 ? avg(sortedBuckets[i - 1][1].pvs) : 0;
              const deltaHours = deltaMinutes / 60;
              const netPowerW = (loadW ?? loadWPrev) - (pvW ?? pvWPrev) - battW;
              cumulativeNetKwh += (netPowerW * deltaHours) / 1000;
            }
          }
          const energyRemainingKwh = oemSocPct != null ? (oemSocPct / 100) * configuredCapacityKwh : null;
          pointsFromBuckets.push({
            time: formatChartTime(date, timeRange, timezone),
            timestamp: ts,
            energyRemaining: energyRemainingKwh,
            netConsumption: cumulativeNetKwh,
            solarPower: pvW != null ? pvW / 1000 : null,
            loadPower: loadW != null ? loadW / 1000 : null,
            batteryPower: battW != null ? -battW / 1000 : null,
            gridPower: gridW != null ? gridW / 1000 : null,
            soc: oemSocPct,
          });
        }

        const pointsWithGaps: TelemetryDataPoint[] = [];
        for (let i = 0; i < pointsFromBuckets.length; i++) {
          const curr = pointsFromBuckets[i];
          if (i > 0) {
            const prev = pointsFromBuckets[i - 1];
            if (curr.timestamp - prev.timestamp > GAP_THRESHOLD_MS) {
              const gapTs = prev.timestamp + GAP_THRESHOLD_MS;
              pointsWithGaps.push({
                time: formatChartTime(gapTs, timeRange, timezone),
                timestamp: gapTs,
                energyRemaining: null,
                netConsumption: null,
                solarPower: null,
                loadPower: null,
                batteryPower: null,
                gridPower: null,
                soc: null,
              });
            }
          }
          pointsWithGaps.push(curr);
        }

        let lastValidSoc: number | null = null;
        let lastValidEnergy: number | null = null;
        const pointsSanitized = pointsWithGaps.map((p) => {
          const soc = p.soc;
          let newSoc: number | null = soc ?? null;
          let newEnergy: number | null = p.energyRemaining;
          if (soc != null && p.energyRemaining != null) {
            if (soc <= 0 && lastValidSoc != null && lastValidSoc > 10) {
              newSoc = null;
              newEnergy = null;
            } else if (lastValidSoc != null && Math.abs(soc - lastValidSoc) > MAX_SOC_DELTA_PCT) {
              newSoc = null;
              newEnergy = null;
            } else if (lastValidEnergy != null && Math.abs(p.energyRemaining - lastValidEnergy) > MAX_ENERGY_DELTA_KWH) {
              newSoc = null;
              newEnergy = null;
            } else {
              lastValidSoc = soc;
              lastValidEnergy = p.energyRemaining;
            }
          } else {
            lastValidSoc = null;
            lastValidEnergy = null;
          }
          return { ...p, soc: newSoc, energyRemaining: newEnergy };
        });

        const sampleRate = timeRange === '24h' ? 1 : 2;
        setChartData(pointsSanitized.filter((_, i) => i % sampleRate === 0));
      } catch (err) {
        if (isAbortError(err)) return;
        console.error('Error fetching telemetry history:', err);
        setError('Failed to load telemetry data');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [propertyId, plantId, timeRange, timezone, configuredCapacityKwh]);

  // When series ends before "now" and we have live values, append a "now" point so the graph and "Current" header match.
  const filteredData = useMemo(() => {
    if (chartData.length === 0) return chartData;
    const last = chartData[chartData.length - 1];
    const nowMs = Date.now();
    if (nowMs - last.timestamp < STALE_SERIES_MS) return chartData;
    if (currentValues?.soc == null && currentValues?.energy == null) return chartData;
    const nowPoint: TelemetryDataPoint = {
      time: formatChartTime(nowMs, timeRange, timezone),
      timestamp: nowMs,
      energyRemaining: currentValues?.energy ?? null,
      netConsumption: last.netConsumption ?? null,
      solarPower: currentValues?.solar ?? last.solarPower ?? null,
      loadPower: currentValues?.load ?? last.loadPower ?? null,
      batteryPower: currentValues?.battery ?? last.batteryPower ?? null,
      gridPower: last.gridPower ?? null,
      soc: currentValues?.soc ?? null,
    };
    return [...chartData, nowPoint];
  }, [chartData, currentValues, timeRange, timezone]);

  const xAxisTicks = useMemo(() => getTelemetryXAxisTicks(filteredData), [filteredData]);

  // Render chart based on view mode
  const renderChart = () => {
    if (viewMode === 'energy') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={filteredData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <defs>
              {/* Design token: --green-primary (rgb 16,185,129 = #10B981). SVG stop-color requires rgba, not hsl(var()). */}
              <linearGradient id="energyFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="rgba(16, 185, 129, 0.2)" />
                <stop offset="95%" stopColor="rgba(16, 185, 129, 0.05)" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border-subtle))" opacity={0.3} />
            <XAxis
              dataKey="time"
              ticks={xAxisTicks.length > 0 ? xAxisTicks : undefined}
              tick={{ fill: 'hsl(var(--text-muted))', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              stroke="hsl(var(--border-default))"
              minTickGap={14}
            />
            <YAxis
              tick={{ fill: 'hsl(var(--text-muted))', fontSize: 11, fontFamily: 'JetBrains Mono' }}
              tickFormatter={(v) => `${Math.round(Number(v))} kWh`}
              allowDecimals={false}
              stroke="hsl(var(--border-default))"
              domain={['dataMin - 5', configuredCapacityKwh * 1.1]}
            />
            <Tooltip content={(p) => <TelemetryTooltip {...p} viewMode="energy" />} />
            <Area
              type="monotone"
              dataKey="energyRemaining"
              fill="url(#energyFill)"
              stroke="hsl(var(--green-primary))"
              strokeWidth={2}
              connectNulls={false}
            />
            {reserveKwh !== undefined && (
              <ReferenceLine
                y={reserveKwh}
                stroke="hsl(var(--amber-muted))"
                strokeWidth={2}
                strokeDasharray="4 4"
              />
            )}
            <Line
              type="monotone"
              dataKey="netConsumption"
              stroke="hsl(var(--slate-blue))"
              strokeWidth={1.5}
              dot={false}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      );
    }

    if (viewMode === 'power') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={filteredData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border-subtle))" opacity={0.3} />
            <XAxis
              dataKey="time"
              ticks={xAxisTicks.length > 0 ? xAxisTicks : undefined}
              tick={{ fill: 'hsl(var(--text-muted))', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              stroke="hsl(var(--border-default))"
              minTickGap={14}
            />
            <YAxis
              tick={{ fill: 'hsl(var(--text-muted))', fontSize: 11, fontFamily: 'JetBrains Mono' }}
              tickFormatter={(v) => `${Math.round(Number(v))} kW`}
              allowDecimals={false}
              stroke="hsl(var(--border-default))"
            />
            <Tooltip content={(p) => <TelemetryTooltip {...p} viewMode="power" />} />
            <ReferenceLine y={0} stroke="hsl(var(--border-default))" strokeWidth={2} />
            <Line
              type="monotone"
              dataKey="solarPower"
              stroke="hsl(var(--solar-gold))"
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="loadPower"
              stroke="hsl(var(--slate-blue))"
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="batteryPower"
              stroke="hsl(var(--text-primary))"
              strokeWidth={2}
              dot={false}
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="gridPower"
              stroke="hsl(var(--muted-foreground))"
              strokeWidth={1.5}
              strokeDasharray="4 2"
              dot={false}
              connectNulls={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      );
    }

    // State view
    return (
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart data={filteredData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
          <defs>
            {/* Design token: --text-primary (#E5E7EB = rgb 229,231,235). SVG stop-color requires rgba, not hsl(var()). */}
            <linearGradient id="stateFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="rgba(229, 231, 235, 0.2)" />
              <stop offset="95%" stopColor="rgba(229, 231, 235, 0.05)" />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border-subtle))" opacity={0.3} />
          <XAxis
            dataKey="time"
            ticks={xAxisTicks.length > 0 ? xAxisTicks : undefined}
            tick={{ fill: 'hsl(var(--text-muted))', fontSize: 10, fontFamily: 'JetBrains Mono' }}
            stroke="hsl(var(--border-default))"
            minTickGap={14}
          />
          <YAxis
            tick={{ fill: 'hsl(var(--text-muted))', fontSize: 11, fontFamily: 'JetBrains Mono' }}
            tickFormatter={(v) => `${Math.round(Number(v))} %`}
            allowDecimals={false}
            stroke="hsl(var(--border-default))"
            domain={[0, 100]}
          />
          <Tooltip content={(p) => <TelemetryTooltip {...p} viewMode="state" />} />
          <Area
            type="monotone"
            dataKey="soc"
            fill="url(#stateFill)"
            stroke="hsl(var(--text-primary))"
            strokeWidth={2}
            connectNulls={false}
          />
          {reserveKwh !== undefined && configuredCapacityKwh > 0 && (
            <ReferenceLine
              y={Math.round((reserveKwh / configuredCapacityKwh) * 100)}
              stroke="hsl(var(--amber-muted))"
              strokeWidth={2}
              strokeDasharray="4 4"
              label={{ value: 'Reserve threshold', position: 'right', fill: 'hsl(var(--amber-muted))', fontSize: 10 }}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  // Must be declared before getChartHeader (which uses it) to avoid TDZ. Use last/first valid (non-null) points.
  const displayCurrentValues = useMemo(() => {
    if (chartData.length === 0 && currentValues) return currentValues;
    if (chartData.length === 0) return undefined;
    const lastValid = [...chartData].reverse().find((p) => p.energyRemaining != null || p.soc != null);
    const firstValid = chartData.find((p) => p.energyRemaining != null || p.soc != null);
    const lastPoint = chartData[chartData.length - 1];
    const firstPoint = chartData[0];
    return {
      energy: currentValues?.energy ?? (lastValid?.energyRemaining ?? lastPoint.energyRemaining ?? undefined),
      periodStart: currentValues?.periodStart ?? (firstValid?.energyRemaining ?? firstPoint.energyRemaining ?? undefined),
      net: currentValues?.net ?? (lastValid?.netConsumption ?? lastPoint.netConsumption ?? undefined),
      solar: currentValues?.solar ?? (lastValid?.solarPower ?? lastPoint.solarPower ?? undefined),
      load: currentValues?.load ?? (lastValid?.loadPower ?? lastPoint.loadPower ?? undefined),
      battery: currentValues?.battery ?? (lastValid?.batteryPower ?? lastPoint.batteryPower ?? undefined),
      soc: currentValues?.soc ?? (lastValid?.soc ?? lastPoint.soc ?? undefined),
      socStart: currentValues?.socStart ?? (firstValid?.soc ?? firstPoint.soc ?? undefined),
      socChange:
        currentValues?.socChange ??
        (lastValid?.soc != null && firstValid?.soc != null ? lastValid.soc - firstValid.soc : undefined),
    };
  }, [chartData, currentValues]);

  // Get chart title and current values based on view mode
  const getChartHeader = () => {
    const values = displayCurrentValues;
    if (viewMode === 'energy') {
      return {
        title: 'Energy Remaining',
        values: [
          { label: 'Current', value: values?.energy, unit: 'kWh' },
          { label: 'Period start', value: values?.periodStart, unit: 'kWh' },
          { label: 'Net', value: values?.net, unit: 'kWh' },
        ],
      };
    }
    if (viewMode === 'power') {
      return {
        title: 'Power Flow',
        values: [
          { label: 'Solar', value: values?.solar, unit: 'kW', color: 'hsl(var(--solar-gold))' },
          { label: 'Load', value: values?.load, unit: 'kW', color: 'hsl(var(--slate-blue))' },
          { label: 'Battery', value: values?.battery, unit: 'kW', color: 'hsl(var(--text-primary))' },
        ],
      };
    }
    return {
      title: 'State of Charge',
      values: [
        { label: 'Current', value: values?.soc, unit: '%' },
        { label: 'Period start', value: values?.socStart, unit: '%' },
        { label: 'Change', value: values?.socChange, unit: '%' },
      ],
    };
  };

  const header = getChartHeader();

  return (
    <div className="bg-wt-bg-secondary border border-border rounded-[10px] p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <span className="font-sans text-sm uppercase tracking-[0.05em] text-secondary font-medium">
          Telemetry Timeline
        </span>
        <div className="flex gap-4 items-center">
          {/* View Tabs */}
          <div className="flex gap-1 bg-wt-bg-tertiary rounded-lg p-1">
            {(['energy', 'power', 'state'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-4 py-2 rounded-md font-sans text-[13px] font-medium transition-all duration-150 ${
                  viewMode === mode
                    ? 'text-foreground bg-background font-semibold'
                    : 'text-muted-foreground bg-transparent hover:text-foreground hover:bg-white/5'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>

          {/* Time Range Buttons */}
          <div className="flex gap-2">
            {(['24h', '7d'] as TimeRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 rounded-md font-sans text-sm font-medium border transition-all duration-150 ${
                  timeRange === range
                    ? 'text-foreground bg-wt-bg-tertiary border-wt-slate-blue'
                    : 'text-muted-foreground bg-transparent border-border hover:border-muted-foreground'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Chart Header */}
      <div className="flex justify-between items-baseline mb-5 pb-4 border-b border-border">
        <div className="font-sans text-sm font-semibold text-foreground">{header.title}</div>
        <div className="flex gap-6 items-baseline">
          {header.values.map((item, idx) => (
            <div key={idx} className="flex items-baseline gap-2">
              <span className="font-sans text-sm text-wt-text-secondary">{item.label}:</span>
              {item.color && (
                <span
                  className="w-2 h-2 rounded-full inline-block"
                  style={{ backgroundColor: item.color }}
                />
              )}
              <span className="font-mono text-lg font-semibold text-foreground">
                {item.value !== undefined ? item.value.toFixed(1) : '--'}
              </span>
              {item.unit && (
                <span className="font-mono text-sm text-wt-text-secondary">{item.unit}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Chart */}
      {loading ? (
        <div className="h-[400px] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-secondary" />
            <span className="font-sans text-sm text-secondary">Loading telemetry data...</span>
          </div>
        </div>
      ) : error ? (
        <div className="h-[400px] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
            <div className="font-sans text-sm text-foreground">{error}</div>
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
              }}
              className="font-sans text-sm text-wt-green-primary hover:text-foreground underline"
            >
              Retry
            </button>
          </div>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="h-[400px] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="font-sans text-sm text-secondary">No telemetry data available</span>
            <span className="font-sans text-sm text-secondary">
              Full 7-day history appears as more telemetry is collected
            </span>
          </div>
        </div>
      ) : (
        <>
          {renderChart()}
          {(() => {
            const hasGaps =
              (viewMode === 'energy' && chartData.some((p) => p.energyRemaining == null)) ||
              (viewMode === 'state' && chartData.some((p) => p.soc == null));
            if (hasGaps) {
              return (
                <p className="mt-3 font-sans text-sm text-wt-text-secondary text-center">
                  Gaps indicate missing or invalid data.
                </p>
              );
            }
            return null;
          })()}
          {timeRange === '7d' && filteredData.length >= 2 && (() => {
            const firstTs = filteredData[0].timestamp;
            const lastTs = filteredData[filteredData.length - 1].timestamp;
            const dataSpanDays = (lastTs - firstTs) / (24 * 60 * 60 * 1000);
            if (dataSpanDays < 6) {
              return (
                <p className="mt-3 font-sans text-sm text-secondary text-center">
                  Showing {dataSpanDays < 1 ? '< 1' : dataSpanDays.toFixed(1)} day{dataSpanDays >= 1 && dataSpanDays < 2 ? '' : 's'} of data. Full 7-day history appears as more telemetry is collected.
                </p>
              );
            }
            return null;
          })()}
        </>
      )}
    </div>
  );
}
